import { Link, useLocation } from 'react-router-dom';

/**
 * Bottom tab bar — the app's primary nav on a phone. The Capacitor shell
 * always renders it; the WEB shell renders it too so a phone browser gets
 * the same app-like frame (CSS hides it at ≥60rem where the desktop rail
 * takes over, and inside a conversation where chat owns the screen).
 *
 * Three tabs, mapped to the app's real top-level routes:
 *   Home  /                       — the dashboard (matches + a glance at the rituals)
 *   You   /reflections            — the private reflections + weekly check-in home
 *   Settings /settings/notifications
 *
 * (The conversation list lives on Home, so there's no separate "Matches" tab
 * yet — a dedicated conversation-history screen would earn a fourth.)
 *
 * Big tap targets, safe-area-inset-bottom padding, an active sage state with
 * a 2px top indicator, and italic-Fraunces labels per the design system.
 */
export function NativeTabBar() {
  const { pathname } = useLocation();

  const isHome = pathname === '/' || pathname.startsWith('/conversations');
  const isYou = pathname.startsWith('/reflections');
  const isSettings = pathname.startsWith('/settings');

  return (
    <nav className="native-tabbar" role="navigation" aria-label="Primary">
      <Link
        to="/"
        className={`native-tab${isHome ? ' on' : ''}`}
        aria-current={isHome ? 'page' : undefined}
      >
        <svg
          className="native-tab-icon"
          viewBox="0 0 24 24"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1V9.5z" />
        </svg>
        <span className="native-tab-label">Home</span>
      </Link>
      <Link
        to="/reflections"
        className={`native-tab${isYou ? ' on' : ''}`}
        aria-current={isYou ? 'page' : undefined}
      >
        <svg
          className="native-tab-icon"
          viewBox="0 0 24 24"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 3.5c.5 3.5 1.5 4.5 5 5-3.5.5-4.5 1.5-5 5-.5-3.5-1.5-4.5-5-5 3.5-.5 4.5-1.5 5-5z" />
        </svg>
        <span className="native-tab-label">You</span>
      </Link>
      <Link
        to="/settings/notifications"
        className={`native-tab${isSettings ? ' on' : ''}`}
        aria-current={isSettings ? 'page' : undefined}
      >
        <svg
          className="native-tab-icon"
          viewBox="0 0 24 24"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 00.3 1.9l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.9-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.9.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.9 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.9l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.9.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.9-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.9V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />
        </svg>
        <span className="native-tab-label">Settings</span>
      </Link>
    </nav>
  );
}
